import React, { useState, useRef, useEffect } from 'react';
import { CameraCapture } from '../auth/CameraCapture';
import { AvatarCropPreview } from '../profile/AvatarCropPreview';
import './molecules.css';

interface ProfileSetupScreenProps {
  email?: string;
  projectName?: string;
  onContinue: (profile: { firstName: string; ageRange: string; region: string; bio?: string; avatarBlob?: Blob }) => void;
}

export const ProfileSetupScreen: React.FC<ProfileSetupScreenProps> = ({
  email = '',
  onContinue,
}) => {
  const [firstName, setFirstName] = useState('');
  const [region, setRegion] = useState('');
  const [bio, setBio] = useState('');

  /* Avatar state */
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [avatarUrl, previewUrl]);

  const handleAvatarFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(file);
    setPreviewBlob(file);
    setPreviewUrl(url);
    setShowPreview(true);
    setShowAvatarMenu(false);
    e.target.value = '';
  };

  const handleCameraCapture = (blob: Blob) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(blob);
    setPreviewBlob(blob);
    setPreviewUrl(url);
    setShowCameraModal(false);
    setShowPreview(true);
  };

  const acceptPreview = (croppedBlob: Blob) => {
    if (avatarUrl) URL.revokeObjectURL(avatarUrl);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const croppedUrl = URL.createObjectURL(croppedBlob);
    setAvatarBlob(croppedBlob);
    setAvatarUrl(croppedUrl);
    setPreviewBlob(null);
    setPreviewUrl(null);
    setShowPreview(false);
  };

  const retakePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl(null);
    setShowPreview(false);
  };

  const initial = firstName.trim() ? firstName.trim()[0].toUpperCase() : (email ? email[0].toUpperCase() : '?');

  const handleSubmit = () => {
    onContinue({
      firstName: firstName.trim(),
      ageRange: '',
      region: region.trim(),
      bio: bio.trim() || undefined,
      avatarBlob: avatarBlob || undefined,
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#8A9A9E' }}>
      {/* Molecule Cluster 1 — Top left, L-shape */}
      <div className="mol-bloom" style={{ position: 'absolute', top: 80, left: 24, pointerEvents: 'none' }}>
        <svg width="55" height="50" viewBox="-40 -10 95 60">
          <circle cx="10" cy="5" r="3" fill="#fff"/>
          <circle cx="10" cy="25" r="4" fill="#fff"/>
          <circle cx="35" cy="25" r="3" fill="#fff"/>
          <line x1="10" y1="5" x2="10" y2="25" stroke="#fff" strokeWidth="2"/>
          <line x1="10" y1="25" x2="35" y2="25" stroke="#fff" strokeWidth="2"/>
          <path d="M7,2 Q-3,-8 -13,2 Q-23,-8 -33,2" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Molecule Cluster 2 — Top right, Triangle with diagonal wave */}
      <div className="mol-bloom-d1" style={{ position: 'absolute', top: 135, right: 24, pointerEvents: 'none' }}>
        <svg width="55" height="55" viewBox="-5 -25 90 80">
          <circle cx="25" cy="6" r="3" fill="#fff"/>
          <circle cx="8" cy="40" r="3" fill="#fff"/>
          <circle cx="45" cy="40" r="3" fill="#fff"/>
          <line x1="25" y1="6" x2="8" y2="40" stroke="#fff" strokeWidth="2"/>
          <line x1="25" y1="6" x2="45" y2="40" stroke="#fff" strokeWidth="2"/>
          <line x1="8" y1="40" x2="45" y2="40" stroke="#fff" strokeWidth="2"/>
          <path d="M28,3 Q36,3 40,-5 Q36,-13 44,-17 Q44,-25 52,-29" stroke="#1a1814" strokeWidth="3" fill="none" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Molecule Cluster 3 — Bottom left, Angled pair with right wave */}
      <div className="mol-bloom-d2" style={{ position: 'absolute', bottom: 32, left: 28, pointerEvents: 'none' }}>
        <svg width="60" height="45" viewBox="-5 0 70 50">
          <circle cx="12" cy="18" r="3" fill="#fff"/>
          <circle cx="42" cy="32" r="4" fill="#fff"/>
          <line x1="12" y1="18" x2="42" y2="32" stroke="#fff" strokeWidth="2"/>
          <path d="M48,28 Q58,18 68,28 Q78,18 88,28" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Molecule Cluster 4 — Bottom right, Single node with wave */}
      <div className="mol" style={{ position: 'absolute', bottom: 45, right: 32, pointerEvents: 'none' }}>
        <svg width="40" height="40" viewBox="-5 -5 50 50">
          <circle cx="20" cy="20" r="4" fill="#fff"/>
          <path d="M24,16 Q32,8 40,16" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Content layer */}
      <div className="relative z-10">
        {/* Progress bar — 3 bars, first active */}
        <div className="flex gap-2 px-6" style={{ marginTop: 55 }}>
          <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]" />
          <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]/20" />
          <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]/20" />
        </div>

        {/* Main content */}
        <div className="mt-[70px] px-6 pb-12">
          {/* Avatar */}
          <div className="flex flex-col items-center mb-5">
            <div
              className="relative cursor-pointer"
              onClick={() => setShowAvatarMenu(!showAvatarMenu)}
            >
              <div className="w-[80px] h-[80px] bg-white/25 rounded-full flex items-center justify-center border-[3px] border-white/40 overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#D4715A] rounded-full flex items-center justify-center border-[3px] border-[#8A9A9E]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="14" rx="2"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
            </div>
            <p className="text-[#1A1814]/50 text-[12px] mt-2.5">Tap to add a photo</p>
          </div>

          {/* Avatar action menu */}
          {showAvatarMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowAvatarMenu(false)} />
              <div className="relative z-50 bg-white/20 backdrop-blur-lg rounded-2xl overflow-hidden mb-4">
                <button
                  className="w-full p-4 text-white text-left flex items-center gap-3 hover:bg-white/10 transition-colors"
                  onClick={() => { setShowAvatarMenu(false); setShowCameraModal(true); }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Take Photo
                </button>
                <button
                  className="w-full p-4 text-white text-left flex items-center gap-3 hover:bg-white/10 transition-colors"
                  onClick={() => { galleryInputRef.current?.click(); setShowAvatarMenu(false); }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21,15 16,10 5,21" />
                  </svg>
                  Choose from Gallery
                </button>
                {avatarUrl && (
                  <button
                    className="w-full p-4 text-[#D4715A] text-left flex items-center gap-3 hover:bg-white/10 transition-colors"
                    onClick={() => {
                      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
                      setAvatarUrl(null);
                      setAvatarBlob(null);
                      setShowAvatarMenu(false);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <polyline points="3,6 5,6 21,6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                    Remove Photo
                  </button>
                )}
              </div>
            </>
          )}

          {/* Hidden file input */}
          <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleAvatarFile} style={{ display: 'none' }} />

          {/* Step label + heading */}
          <div className="text-center mb-5 anim-stagger-1">
            <span className="text-[#1A1814]/60 text-[11px] font-medium tracking-[1.5px]">STEP 1 OF 3</span>
            <h1 className="text-[#1A1814] text-[26px] font-semibold mt-1.5 leading-[1.2]">
              Set up your profile
            </h1>
            <p className="text-[#1A1814]/60 text-[14px] mt-2">
              Help researchers know who they're hearing from.
            </p>
          </div>

          {/* Display Name field */}
          <div className="bg-white/95 rounded-[14px] py-3.5 px-4 mb-3 anim-stagger-2">
            <label className="block text-[#6B7A7E] text-[11px] font-semibold tracking-[0.5px] mb-1.5">Display name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40"
              placeholder="How should we call you?"
            />
          </div>

          {/* Location field */}
          <div className="bg-white/95 rounded-[14px] py-3.5 px-4 mb-3">
            <label className="block text-[#6B7A7E] text-[11px] font-semibold tracking-[0.5px] mb-1.5">Where are you based?</label>
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40"
              placeholder="City, country"
            />
          </div>

          {/* Bio field */}
          <div className="bg-white/95 rounded-[14px] py-3.5 px-4 mb-4">
            <label className="block text-[#6B7A7E] text-[11px] font-semibold tracking-[0.5px] mb-1.5">A bit about you</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={2}
              className="w-full bg-transparent text-[#1A1814] text-base outline-none placeholder:text-[#1A1814]/40 resize-none leading-relaxed"
              placeholder="Share something that helps researchers understand your perspective..."
            />
          </div>

          {/* Info hint */}
          <div className="flex items-center gap-2 mb-5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(26,24,20,0.5)" strokeWidth="1.5" className="flex-shrink-0">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            <span className="text-[#1A1814]/50 text-[13px]">All fields help but none are required</span>
          </div>

          {/* Continue button */}
          <button
            onClick={handleSubmit}
            className="w-full bg-[#1A1814] text-[#F5F3F0] border-none rounded-xl py-4 text-base font-medium cursor-pointer flex items-center justify-center gap-2 anim-stagger-3"
          >
            Continue
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>

          {/* Skip link */}
          <p className="text-center mt-3.5">
            <button
              onClick={() => onContinue({ firstName: firstName.trim() || 'Participant', ageRange: '', region: '', avatarBlob: avatarBlob || undefined })}
              className="text-[#1A1814]/50 text-[13px] underline bg-transparent border-none cursor-pointer"
            >
              Skip for now
            </button>
          </p>
        </div>
      </div>

      {/* Photo Preview Modal — drag + zoom crop */}
      {showPreview && previewUrl && (
        <AvatarCropPreview
          sourceUrl={previewUrl}
          onAccept={acceptPreview}
          onRetake={retakePreview}
        />
      )}

      <CameraCapture
        isOpen={showCameraModal}
        onCapture={handleCameraCapture}
        onClose={() => setShowCameraModal(false)}
      />
    </div>
  );
};

export default ProfileSetupScreen;
